// icons
// ToDo: replace icons with rtls-ui-components lib
import ExpandLess from '../../rtls-ui-components/components/component-icons/expand-less.vue';
import ExpandMore from '../../rtls-ui-components/components/component-icons/expand-more.vue';
import DatePicker from '../../rtls-ui-components/components/component-icons/date-picker.vue';
import AddBox from '../../rtls-ui-components/components/component-icons/add.vue';
import { h } from 'vue';
import { createVuetify } from 'vuetify';
import { VuetifyDateAdapter } from 'vuetify/date/adapters/vuetify';
import { mdi } from 'vuetify/iconsets/mdi';
import '@mdi/font/css/materialdesignicons.css';
import * as LucideIcons from '@lucide/vue';

import { STYLE_SCOPE_CLASS } from './webshell-embed/embedded';
import 'vuetify/styles';

let weekNumberResolver;

/**
 * Registers the resolver used by the date adapter to provide client week numbers.
 *
 * @param {function(Date): (number|null|undefined)} resolver - Week number resolver.
 */
const setWeekNumberResolver = resolver => {
    weekNumberResolver = resolver;
};

/**
 * Date adapter that uses client-specific week numbers when available.
 */
class PromoDateAdapter extends VuetifyDateAdapter {
    getWeek(date, firstDayOfWeek, firstDayOfYear) {
        const customWeek = weekNumberResolver?.(date);
        if (Number.isInteger(customWeek)) {
            return customWeek;
        }
        return super.getWeek(date, firstDayOfWeek, firstDayOfYear);
    }
}

// importing icons
const iconComponents = import.meta.glob(['./components/component-icons/**/*.vue'], { eager: true });

// helper to take name of component from the path
const pathToComponentName = path =>
    path
        .split('/')
        .pop()
        .replace(/\.\w+$/, '');

// create set of custom icons
const customSvgIconComponents = {
    expand_less: ExpandLess,
    expand_more: ExpandMore,
    date_picker: DatePicker,
    add_box: AddBox,
    ...Object.entries(iconComponents).reduce((acc, [key, value]) => {
        const name = pathToComponentName(key);
        acc[name] = value.default;
        return acc;
    }, {}),
};

const customSVGs = {
    component: props => h(customSvgIconComponents[props.icon]),
};

// Create the custom icon set wrapper for Lucide
const lucideIconSet = {
    component: props => {
        // Vuetify passes the icon string without the prefix (e.g., 'arrow-left')
        const iconName =
            typeof props.icon === 'string'
                ? props.icon
                      .split('-')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join('')
                : props.icon;

        const LucideIcon = LucideIcons[iconName];
        return LucideIcon ? h(LucideIcon, { ...props }) : null;
    },
};

// Sharing a document with the shell's own Vuetify, the two theme stylesheets need distinct ids and
// scopes or they overwrite each other. See rtls-one-retail/frontend/shell/docs/integrations/promo.md.
const createPromoVuetify = ({ embedded = false } = {}) =>
    createVuetify({
        date: {
            adapter: PromoDateAdapter,
        },
        // Overlays teleport to `<body>` by default, which is outside promo's CSS scope and leaves
        // them unstyled. Attach them to promo's own root instead.
        defaults: {
            global: { attach: `.${STYLE_SCOPE_CLASS}` },
        },
        theme: {
            ...(embedded
                ? {
                      stylesheetId: 'vuetify-theme-stylesheet-promo',
                      scope: `.${STYLE_SCOPE_CLASS}`,
                  }
                : {}),
            themes: {
                light: {
                    colors: {
                        // Setting primary app colours for material theme
                        primary: '#2f477c',
                        accent: '#2f477c',
                        error: '#ff5252',
                    },
                    variables: {
                        // place for redefine variables
                        'medium-emphasis-opacity': 1,
                        'tooltip-font-size': '1.2rem',
                    },
                },
            },
        },
        icons: {
            defaultSet: 'mdi',
            sets: {
                mdi,
                custom: customSVGs,
                lucide: lucideIconSet,
            },
        },
    });

export { createPromoVuetify, pathToComponentName, setWeekNumberResolver };
